import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { ActivityItem } from "@/workshop/components/panels/activity-item";
import type { ActivityItemProps } from "@/workshop/components/panels/activity-item";
import { TaskItem } from "@/workshop/components/panels/task-item";
import type { TaskItemProps } from "@/workshop/components/panels/task-item";

/**
 * Workshop: Space panel — right panel showing Activity timeline and Tasks queue.
 *
 * Replaces the old RightPanel (Details/Logs/Actions/Chat tabs) when no
 * interactive frame is selected. Shows space-scoped data.
 */

type SpacePanelTab = "activity" | "tasks";

interface SpacePanelProps {
  activeTab?: SpacePanelTab;
  onTabChange?: (tab: SpacePanelTab) => void;
  onClose?: () => void;
  /** Activity feed entries */
  activities?: ActivityItemProps[];
  /** Task queue entries */
  tasks?: TaskItemProps[];
  /** Count badge for tasks tab (e.g. pending count) */
  taskCount?: number;
  className?: string;
  "data-testid"?: string;
}

function TabButton({
  label,
  isActive,
  count,
  onClick,
  testId,
}: {
  label: string;
  isActive: boolean;
  count?: number;
  onClick: () => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      className={cn(
        "flex flex-1 items-center justify-center gap-1 rounded-lg py-1.5 text-center text-[11px] font-medium transition-colors",
        isActive
          ? "bg-glass-fill-heavy text-primary"
          : "text-muted hover:bg-glass-stroke-subtle hover:text-secondary",
      )}
      onClick={onClick}
      data-testid={testId}
      aria-selected={isActive}
      role="tab"
    >
      {label}
      {count !== undefined && count > 0 && (
        <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-accent/15 px-1 text-[9px] font-semibold text-accent">
          {count}
        </span>
      )}
    </button>
  );
}

export function SpacePanel({
  activeTab = "activity",
  onTabChange,
  onClose,
  activities = [],
  tasks = [],
  taskCount,
  className,
  "data-testid": testId = "space-panel",
}: SpacePanelProps) {
  const pendingCount = taskCount ?? tasks.filter((t) => t.status !== "running").length;

  return (
    <aside
      className={cn(
        "flex w-60 flex-col rounded-2xl border border-glass-stroke bg-glass-fill shadow-glass-sm backdrop-blur-glass-sm",
        className,
      )}
      data-testid={testId}
    >
      {/* Header */}
      <div className="flex h-11 items-center gap-2 border-b border-glass-stroke-subtle px-3.5">
        <Icons.Layers className="h-3.5 w-3.5 shrink-0 text-accent" aria-hidden="true" />
        <span className="text-xs font-semibold text-primary">Space</span>
        <div className="flex-1" />
        <button
          type="button"
          onClick={onClose}
          className="flex items-center justify-center rounded-md p-0.5 text-muted transition-colors hover:text-primary"
          data-testid="space-panel-close"
          aria-label="Close panel"
        >
          <Icons.X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Tab bar */}
      <div
        className="flex gap-1 border-b border-glass-stroke-subtle px-2 py-1"
        role="tablist"
        aria-label="Space panel tabs"
      >
        <TabButton
          label="Activity"
          isActive={activeTab === "activity"}
          onClick={() => onTabChange?.("activity")}
          testId="space-panel-tab-activity"
        />
        <TabButton
          label="Tasks"
          isActive={activeTab === "tasks"}
          count={pendingCount}
          onClick={() => onTabChange?.("tasks")}
          testId="space-panel-tab-tasks"
        />
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === "activity" && (
          <div className="flex flex-col px-2 py-1" data-testid="space-panel-activity">
            {activities.length > 0 ? (
              activities.map((item, i) => (
                <ActivityItem
                  key={i}
                  {...item}
                  data-testid={`activity-${i}`}
                />
              ))
            ) : (
              <p className="px-2 py-6 text-center text-[10px] text-muted">
                No activity yet
              </p>
            )}
          </div>
        )}

        {activeTab === "tasks" && (
          <div className="flex flex-col" data-testid="space-panel-tasks">
            {tasks.length > 0 ? (
              tasks.map((task, i) => (
                <TaskItem
                  key={i}
                  {...task}
                  data-testid={`task-${i}`}
                />
              ))
            ) : (
              <p className="px-4 py-6 text-center text-[10px] text-muted">
                No tasks in queue
              </p>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}

export type { SpacePanelProps, SpacePanelTab };
